import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type {
  CreateEmployeeInput,
  CreatedEmployee,
  EmployeeAdminView,
  EmployeeProfile,
  UpdateEmployeeInput,
} from '@shared/schemas/employee';
import type { ActivityFilter, ListResponse, PeopleSort } from '@shared/schemas/pagination';
import type { PositionView } from '@shared/schemas/position';

/* The queries behind /admin/employees. ROADMAP.md §3.4. */

const ROOT = ['admin', 'employees'] as const;

export interface SkillView {
  id: string;
  name: string;
  isActive: boolean;
  employeeCount: number;
}

export interface EmployeeListParams {
  sort: PeopleSort;
  page: number;
  search: string;
  activity: ActivityFilter;
  positionId: string;
  skillId: string;
}

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

/**
 * One cleaner's record with the numbers behind it — `/admin/employees/:id`.
 *
 * The job history on that page is **not** in here: it is `useJobs` with an
 * `employeeId`, which is the same query the jobs screen runs. One answer to
 * "which jobs is this person on", not two.
 */
export function useEmployeeProfile(id: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'profile', id],
    queryFn: () => api.get<EmployeeProfile>(`/api/admin/employees/${id ?? ''}/profile`),
    enabled: id !== undefined,
  });
}

export function useEmployees(params: EmployeeListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<EmployeeAdminView>>(
        `/api/admin/employees${queryString({
          page: params.page,
          sort: params.sort,
          search: params.search,
          activity: params.activity,
          positionId: params.positionId,
          skillId: params.skillId,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * Positions and skills for the form's dropdowns, in one request.
 *
 * A long `staleTime` because these change when somebody edits a catalog, not
 * while a form is open — and the form cannot render until both have arrived.
 */
export function useEmployeeFormOptions() {
  return useQuery({
    queryKey: [...ROOT, 'form-options'],
    queryFn: () =>
      api.get<{ positions: PositionView[]; skills: SkillView[] }>(
        '/api/admin/employees/form-options',
      ),
    staleTime: 5 * 60_000,
  });
}

export function useCreateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEmployeeInput) =>
      api.post<CreatedEmployee>('/api/admin/employees', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateEmployee(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEmployeeInput) =>
      api.patch<EmployeeAdminView>(`/api/admin/employees/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useSetEmployeeActive(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      api.post<EmployeeAdminView>(
        `/api/admin/employees/${id}/${isActive ? 'reactivate' : 'deactivate'}`,
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useCreateSkill() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<SkillView>('/api/admin/employees/skills', { name }),
    onSuccess: () => invalidate(client),
  });
}
